/**
 * «Распределение по ЦА» (owner wave 5, task T.W1; grid reworked to the owner's own
 * sheet, wave 6; ONE column per course id since T.Z35).
 *
 * One pivot, one toggle: the venues that already hold an identified application
 * (LINK_RECEIVED / EVI_PASSED) against the 12 course columns — one per campaign course
 * id, the owner's canonical table — every cell lit against the venue's plan for the
 * format the course runs. The api still answers the (course × format) cross of the frozen
 * #11.5 contract; the page collapses it into the 12 columns (`courseColumns`), so a
 * course's applications sit in its one column whatever format the source tagged them
 * with — the split the owner read as «offline & online counted incorrectly». A cell click
 * drills into the applications register with the venue, the course AND the counted
 * statuses the cell was counted from (no format — the column aggregates both;
 * ADDITIVE v1.9.33 / T.Z141 — `status=LINK_RECEIVED,EVI_PASSED` rides along, so a «1»
 * opens exactly the one application the cell counted), so the numbers behind the colour
 * are always the list the click lands on.
 *
 * THE HEADER — two rows, the spreadsheet's own shape since T.Z35c: the first row is ONE
 * MERGED cell per caption run («ИИ 8-9», «ИИ 10-11», «ИИ СПО», «Python 8-9», «Python 10-11»,
 * «Python СПО» — `colSpan=2`, the офл/онл pair of one course under one caption, the owner's
 * own sheet), written HORIZONTALLY — two 40px columns give the text 80px, which is why the
 * T.X3 vertical captions are gone; a longer name wraps to two lines instead of forcing the
 * columns wider. The second row is the «офл | онл» pair — the one thing that still tells
 * the two columns of one caption apart. «Площадки» and «Итого» span both rows. No id suffix
 * beside the caption (T.Z35): the owner deleted the «ИИ 8-9 31599» gluing; the id lives in
 * the merged cell's tooltip, one «id — ДОП title» line per course under the caption.
 *
 * THE CROSSHAIR — hovering a cell lights its whole row and its whole column (a brand
 * tint at 10%, layered over the traffic light, never replacing it). It is painted
 * directly on the DOM (`highlight`), because a state per mousemove would re-render two
 * hundred rows to move one highlight.
 *
 * THE VENUE FILTER (T.V7) — a full-width combobox over the shared venue directory (#6,
 * the same read the quotas and applications pages search), narrowing the pivot to one
 * venue. It is CLIENT-SIDE on purpose: `/ca-distribution` takes `excludeColleges` only —
 * any other query key is 400 VALIDATION — and 189 rows filter in a millisecond. Because
 * the filter never reaches the api, the totals row is recomputed from the rows that
 * survived it (`totalsOfRows`): a totals row that kept counting the venues the filter
 * removed would contradict the row above it.
 *
 * THE «ОТДЕЛ» FILTER (ADDITIVE, T.Z56) — the second client-side cut, over the same rows.
 * A venue's department is its managers' departments (`src/departments.ts`, the T.Z42
 * fold; `useDepartmentJoin` reads the #6 × #25.5 halves) and the endpoint answers the
 * whole roster in one read, so the cut happens in the browser and the totals recompute
 * with it — the paged/aggregate surfaces (#3/#7.5/#11) narrow the same buckets
 * SERVER-SIDE instead. The merged two-tier header is built from the course dictionary,
 * never from the rows, so its `colSpan` pairs are untouched by any row filter.
 */

import { useLayoutEffect, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router';

import type { CaRow, CaTotals } from '../../api/ca-distribution';
import { useCaDistribution } from '../../api/ca-distribution';
import { Combobox } from '../../components/Combobox';
import type { ComboboxOption } from '../../components/combobox-model';
import { ErrorState, Select, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';
import { formatCount } from '../dashboard/format';
import { DEPARTMENT_BUCKET_LABEL, departmentFilterOptions } from '../../departments';
import { useDepartmentJoin } from '../../departments-query';
import { usePrograms, useVenueDirectory } from '../quotas/dictionary';
import { venueCardHref, venueComboboxOptions } from '../quotas/quotas-model';
import './ca-distribution.css';
import {
  PARAM_DEPARTMENT,
  PARAM_EXCLUDE_COLLEGES,
  PARAM_VENUE_ID,
  cellHref,
  columnCount,
  columnPlan,
  courseColumns,
  courseGroups,
  excludeParamValue,
  filterRowsByDepartment,
  filterRowsByVenue,
  parseDepartmentParam,
  parseExcludeParam,
  parseVenueIdParam,
  totalsOfRows,
  trafficHint,
  trafficLight,
  totalsCellLight,
  audienceWarningHint,
  type CourseColumn,
} from './ca-distribution-model';

export function CaDistributionPage() {
  const { params, setParams } = useQueryParams();
  // Absent = ON: «исключить колледжи» is the page's default (the meeting's problem is in
  // the schools), so a bare /ca-distribution link opens the schools view.
  const exclude = parseExcludeParam(params[PARAM_EXCLUDE_COLLEGES]);
  // Absent = «Все площадки» (T.V7): the pivot is the neutral read, the filter narrows it.
  const venueId = parseVenueIdParam(params[PARAM_VENUE_ID]);
  // ADDITIVE (T.Z56) — absent = «Все отделы».
  const department = parseDepartmentParam(params[PARAM_DEPARTMENT]);
  const query = useCaDistribution(exclude);

  // The «Отдел» join: `venueId → bucket` over the venue directory × the departments.
  const { buckets: departmentBuckets, counts: departmentCounts } = useDepartmentJoin();

  // #21 gives the columns their short labels; the pivot answers the ids only, and the
  // labels must be the same vocabulary the quota grid prints (T.R5), never a re-invention.
  const programs = usePrograms();
  // #6 — the directory the «Площадка» combobox searches (names AND addresses), the same
  // read the quotas and applications filters use. `retry: 0`, so a missing endpoint costs
  // one request: the filter then narrows by nothing but still shows a URL-carried id.
  const directory = useVenueDirectory();

  const columns = useMemo(
    () => courseColumns(query.data?.courses ?? [], programs.data?.data ?? []),
    [query.data?.courses, programs.data],
  );

  // The venue cut and the department cut are applied AFTER the query, never in it: the
  // api's answer stays the whole pivot, so clearing either filter is instant and free.
  const rows = useMemo(
    () => filterRowsByDepartment(filterRowsByVenue(query.data?.rows ?? [], venueId), departmentBuckets, department),
    [query.data?.rows, venueId, departmentBuckets, department],
  );
  const totals = useMemo(
    () =>
      query.data === undefined
        ? undefined
        : venueId === undefined && department === undefined
          ? query.data.totals
          : totalsOfRows(rows, query.data.courses),
    [query.data, venueId, department, rows],
  );

  // What the selected venue is called, for the meta line and for an option the directory
  // does not know yet (a deep link arriving before #6 answers, or a venue it dropped).
  const directoryName = venueId !== undefined ? directory.data?.find((venue) => venue.id === venueId)?.name : undefined;
  const venueLabel = venueId === undefined ? undefined : (directoryName ?? rows[0]?.venueName ?? `Площадка ${venueId}`);

  const venueOptions = useMemo<ComboboxOption[]>(() => {
    const options = venueComboboxOptions(directory.data ?? []);
    if (venueId === undefined || venueLabel === undefined) return options;
    return options.some((option) => option.value === String(venueId))
      ? options
      : [{ value: String(venueId), label: venueLabel }, ...options];
  }, [directory.data, venueId, venueLabel]);

  return (
    <>
      <div className="page-header">
        <h1>Распределение по ЦА</h1>
        <p className="page-header__sub">
          Заявления, определившиеся по площадке (ссылка получена или ЕВИ пройден), по программам и форматам —
          против плана площадки на каждый формат.
        </p>
      </div>

      <div className="ca-toolbar">
        {/* Full width on purpose (T.V7): a venue name plus an address is a 60-plus-
            character label, the row the quotas and applications filters already take. */}
        <div className="ca-toolbar__filter">
          <label className="ca-toolbar__label" htmlFor="ca-venue">
            Площадка
          </label>
          <Combobox
            id="ca-venue"
            testId="ca-venue-filter"
            value={venueId !== undefined ? String(venueId) : ''}
            options={venueOptions}
            onChange={(next) => setParams({ [PARAM_VENUE_ID]: next === '' ? undefined : next })}
            placeholder="Все площадки — найдите по названию или адресу"
            emptyText="Площадка не найдена"
            clearLabel="Показать все площадки"
            resultsLabel={(count) => `Найдено площадок: ${count}`}
          />
        </div>
        {/* ADDITIVE (T.Z56) — «Отдел»: the buckets a venue reaches only through its
            managers; client-side here, like the venue combobox above. */}
        <div className="ca-toolbar__filter ca-toolbar__filter--department">
          <label className="ca-toolbar__label" htmlFor="ca-department">
            Отдел
          </label>
          <Select
            id="ca-department"
            data-testid="ca-department"
            value={department ?? ''}
            onChange={(event) => setParams({ [PARAM_DEPARTMENT]: event.target.value || undefined })}
          >
            {departmentFilterOptions(departmentCounts).map((option) => (
              <option key={option.value} value={option.value} title={option.title}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
        <label className="ca-toolbar__toggle">
          <input
            type="checkbox"
            checked={exclude}
            onChange={(event) =>
              // `true` is the default, so it REMOVES the key — the default state keeps the
              // clean URL, the way every other filter on this app does.
              setParams({ [PARAM_EXCLUDE_COLLEGES]: event.currentTarget.checked ? undefined : excludeParamValue(false) })
            }
            data-testid="ca-exclude-colleges"
          />
          <span>Исключить колледжи</span>
        </label>
        {totals === undefined ? null : (
          <span className="ca-toolbar__meta" data-testid="ca-meta">
            {describeState(rows, exclude, venueLabel)}
            {department !== undefined ? ` · ${DEPARTMENT_BUCKET_LABEL[department]}` : ''}
          </span>
        )}
      </div>

      {query.isPending ? (
        <Spinner label="Загрузка распределения" />
      ) : query.isError ? (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Не удалось загрузить распределение'}
          onRetry={() => void query.refetch()}
        />
      ) : totals === undefined ? null : (
        <CaTable columns={columns} rows={rows} totals={totals} />
      )}
    </>
  );
}

/**
 * The one line of scope above the table: how many rows are ON SCREEN, and what cut them
 * down to that. Under a venue filter the college count is meaningless (one row is on
 * screen), so the filter names itself instead of the toggle's scope.
 */
function describeState(rows: CaRow[], exclude: boolean, venueName: string | undefined): string {
  const colleges = rows.filter((row) => row.isCollege).length;
  const scope =
    venueName !== undefined ? `площадка: ${venueName}` : exclude ? 'без колледжей' : 'все площадки';
  // With the toggle ON the api has already dropped those rows, so there is nothing to count
  // here — the line stays honest by naming the scope instead of a hidden number.
  const extra = !exclude && venueName === undefined && colleges > 0 ? ` · из них колледжей: ${colleges}` : '';
  return `${rows.length} пл. · ${scope}${extra}`;
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

function CaTable({
  columns,
  rows,
  totals,
}: {
  columns: CourseColumn[];
  rows: CaRow[];
  totals: CaTotals;
}) {
  // T.Z35c — ONE merged header cell per caption run: «ИИ 8-9» spans its офл/онл pair.
  const groups = useMemo(() => courseGroups(columns), [columns]);
  // The crosshair (hover a cell → its whole row AND column light up) is painted
  // straight on the DOM: a React state per mousemove would re-render two hundred
  // rows to move a highlight one cell over.
  const tableRef = useRef<HTMLTableElement>(null);
  // T.Z25 — the SECOND header row sticks at exactly the height of what sits above it, or
  // the two slide over each other once the grid scrolls. T.Z35c — both bands are measured,
  // and each from the cells that belong to it ALONE: a rowSpan=2 cell («Площадки»,
  // «Итого») reaches into the other row, and a row element's own rect overshoots through
  // it (the T.Z35 lesson). The stylesheet's 72px/26px stand in for the first paint only.
  const captionRowRef = useRef<HTMLTableRowElement>(null);
  const tagRowRef = useRef<HTMLTableRowElement>(null);

  useLayoutEffect(() => {
    const captionRow = captionRowRef.current;
    const tagRow = tagRowRef.current;
    const table = tableRef.current;
    if (!captionRow || !tagRow || !table || typeof ResizeObserver === 'undefined') return;
    // A band's height: the tallest cell of the row that spans ONE row only.
    const bandHeight = (row: HTMLTableRowElement) => {
      let height = 0;
      for (const cell of row.cells) {
        if (cell.rowSpan !== 1) continue; // a rowSpan=2 cell belongs to neither band alone
        height = Math.max(height, cell.getBoundingClientRect().height);
      }
      return height;
    };
    const apply = () => {
      const captions = Math.ceil(bandHeight(captionRow));
      const tags = Math.ceil(bandHeight(tagRow));
      // `--ca-head-1h` is the height the WHOLE sticky header occupies (both rows) — the
      // number the stylesheet builds its sticky stack from. `--ca-head-2h` is the tag
      // row's own band, which row 2 subtracts from that stack: row 2 must sit UNDER row 1,
      // not under the whole header.
      table.style.setProperty('--ca-head-1h', `${captions + tags}px`);
      table.style.setProperty('--ca-head-2h', `${tags}px`);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(captionRow);
    observer.observe(tagRow);
    return () => observer.disconnect();
  }, [columns]);

  const onCellOver = (event: MouseEvent<HTMLTableElement>) => {
    const table = tableRef.current;
    if (!table) return;
    const target = event.target as HTMLElement;
    const cell = target.closest<HTMLElement>('td[data-col], th[data-col]');
    highlight(table, cell?.dataset.col ?? null, cell?.closest('tr') ?? null);
  };

  const onCellLeave = () => {
    const table = tableRef.current;
    if (table) highlight(table, null, null);
  };

  return (
    <TableWrap>
      <table
        className="data ca-grid"
        data-testid="ca-grid"
        ref={tableRef}
        onMouseOver={onCellOver}
        onMouseLeave={onCellLeave}
      >
        <thead>
          <tr ref={captionRowRef}>
            <th scope="colgroup" rowSpan={2} className="ca-grid__venue">
              Площадки
            </th>
            {groups.map((group) => (
              <th
                scope="colgroup"
                // Captions are unique per group by construction (equal neighbours merge),
                // so the caption is a stable key.
                key={group.caption}
                className="ca-grid__course"
                colSpan={group.columns.length}
                // T.V3 — the FULL courses beside their ids, one line per column under the
                // caption: «31599 — Разработчик интеллектуальных решений…». The caption is
                // short by design, so this tooltip is the one place it resolves to a ДОП.
                // T.Z37 — a flagged course adds its ⚠ line (the share, the counts and the
                // declared audience) under its own title line.
                title={group.title}
                data-testid="ca-course-head"
                data-caption={group.caption}
                data-col-span={group.columns.length}
                data-audience-warning={group.audienceWarning !== null ? 'true' : undefined}
              >
                {group.caption}
                {/* T.Z37 — the audience-swap guard's marker: the machine caught what the
                    partner's complaint caught in September. Nothing else on the page moves. */}
                {group.audienceWarning !== null ? (
                  <span
                    className="ca-grid__warn"
                    data-testid="ca-audience-warning"
                    title={audienceWarningHint(group.audienceWarning)}
                  >
                    ⚠
                  </span>
                ) : null}
              </th>
            ))}
            <th scope="col" rowSpan={2} className="num ca-grid__total-head">
              Итого
            </th>
          </tr>
          <tr ref={tagRowRef}>
            {columns.map((column, index) => (
              <th
                scope="col"
                key={column.courseId}
                className="ca-grid__cell-head"
                title={column.format === undefined ? column.title : `${column.title} · ${column.format === 'OFFLINE' ? 'офлайн' : 'онлайн'}`}
                // The column's position in the grid (the crosshair reads it), its course
                // (the caption cell above spans two ids) and its FORMAT (the hook the
                // blue/green tint hangs on — absent while #21 has not named one).
                data-col={index}
                data-course-id={column.courseId}
                data-format={column.format}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.venueId} data-testid="ca-row" data-venue-id={row.venueId} data-is-college={row.isCollege}>
              <th scope="row" className="ca-grid__venue" title={row.isCollege ? `${row.venueName} · колледж` : row.venueName}>
                {/* T.Z35c — the name is a link to the venue card (`/venues?id=`, the same
                    id every drill link on this row carries, same tab, the quota grid's
                    own `venueCardHref`): the grid answers «сколько», the card answers
                    «что с этим делать». Styled like that grid's link-on-name — the row's
                    own type, the underline on hover — not a button. The badge and the
                    tooltip stay on the cell, untouched by the link. */}
                <Link className="ca-grid__venue-link" to={venueCardHref(row.venueId)} data-testid="ca-venue-link">
                  {row.venueName}
                </Link>
                {row.isCollege ? (
                  <span className="ca-grid__college" data-testid="ca-college-badge">
                    колледж
                  </span>
                ) : null}
              </th>
              {columns.map((column, index) => (
                <CaCell key={column.courseId} col={index} row={row} column={column} />
              ))}
              <td className="num ca-grid__total" data-testid="ca-row-total">
                {formatCount(row.total)}
              </td>
            </tr>
          ))}
          <tr className="ca-grid__totals" data-testid="ca-totals-row">
            <th scope="row" className="ca-grid__venue">
              Итого
            </th>
            {columns.map((column, index) => {
              const count = columnCount(totals, column);
              const plan = columnPlan(totals, column);
              const light = totalsCellLight(totals, column);
              return (
                <td
                  key={column.courseId}
                  className={`num ca-grid__cell ca-grid__cell--${light}`}
                  data-testid="ca-total-cell"
                  data-course-id={column.courseId}
                  data-col={index}
                  data-light={light}
                  data-format={column.format}
                  title={trafficHint(count, plan)}
                >
                  {count > 0 ? formatCount(count) : ''}
                </td>
              );
            })}
            <td className="num ca-grid__total" data-testid="ca-grand-total">
              {formatCount(totals.total)}
            </td>
          </tr>
        </tbody>
      </table>
      <p className="ca-hint">
        Зелёный — до 80% плана формата, оранжевый — 80–99%, красный — план выбран или плана нет, а заявления есть.
        Пустая ячейка — заявлений этого курса нет. Клик по ячейке открывает эти заявления в реестре.
      </p>
    </TableWrap>
  );
}

/**
 * The crosshair: the column the pointer is on gets `.ca-hl-col` on every cell that
 * carries that `data-col`, the row gets `.ca-hl-row`. Clearing is unconditional and
 * total — a column is four cells (sub-header, the venue rows, the totals row), so a
 * "clear the first match" would leave the rest lit behind the pointer. `null` (the
 * pointer left the table) clears only.
 */
function highlight(table: HTMLTableElement, col: string | null, row: HTMLTableRowElement | null) {
  table.querySelectorAll('.ca-hl-col').forEach((cell) => cell.classList.remove('ca-hl-col'));
  table.querySelectorAll('.ca-hl-row').forEach((row) => row.classList.remove('ca-hl-row'));

  if (col !== null) {
    table.querySelectorAll(`[data-col="${col}"]`).forEach((cell) => cell.classList.add('ca-hl-col'));
  }
  row?.classList.add('ca-hl-row');
}

function CaCell({
  col,
  row,
  column,
}: {
  col: number;
  row: CaRow;
  column: CourseColumn;
}) {
  const count = columnCount(row, column);
  const plan = columnPlan(row, column);
  const light = trafficLight(count, plan);
  return (
    <td
      className={`num ca-grid__cell ca-grid__cell--${light}`}
      data-testid="ca-cell"
      data-course-id={column.courseId}
      data-col={col}
      data-light={light}
      data-format={column.format}
      title={trafficHint(count, plan)}
    >
      {/* An empty cell stays empty: «0» in 200+ of the 2 400 cells is noise the
          owner read as a second header, and absence is what the blank says. */}
      {count > 0 ? (
        <Link className="ca-grid__link" to={cellHref(row.venueId, column.courseId)} data-testid="ca-cell-link">
          {formatCount(count)}
        </Link>
      ) : null}
    </td>
  );
}
